import React, { useEffect, useState } from 'react';
/* global axios */
import api from '../services/apiService';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';  // Import von React Helmet
import Select from 'react-select';

const WorkshopOrderList = () => {
  const [workshopOrders, setWorkshopOrders] = useState([]);
  const [invoiceStates, setInvoiceStates] = useState({});
  const [invoices, setInvoices] = useState([]);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [serverFilterOptions, setServerFilterOptions] = useState({
    workshopOrderNumbers: [],
    statuses: [],
    dates: [],
    plannedExecuteDates: [],
    customers: [],
    workshopOrderTypes: [],
    orderNumbers: [],
    blockedPeriodFromDates: [],
    blockedPeriodToDates: [],
    fulfillmentDates: [],
  });

  // Initialer Filter-State (wird aus Local Storage geladen oder mit leeren Werten initialisiert)
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("workshopOrderFilters");
    return saved
      ? JSON.parse(saved)
      : {
          workshopOrderNumber: null,
          status: null,
          date: null,
          plannedExecuteDate: null,
          customer: null,
          workshopOrderType: null,
          orderNumber: null,
          blockedPeriodFrom: null,
          blockedPeriodTo: null,
          fulfillmentDate: null,
        };
  });

   // Filter in Local Storage speichern und Seite zurücksetzen bei Filteränderungen
   useEffect(() => {
    localStorage.setItem("workshopOrderFilters", JSON.stringify(filters));
    setCurrentPage(1);
  }, [filters]);

  // Serverseitige Filteroptionen laden (Implementierung analog zu offer/filter-options)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Beispiel: Eigener Endpunkt, der die Filteroptionen für WorkshopOrders bereitstellt.
        const response = await api.get('/workshoporders/filter-options');
        setServerFilterOptions(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Filteroptionen:', error);
      }
    };
    fetchFilterOptions();
  }, []);

   // Pagination
   const [currentPage, setCurrentPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const limit = 25;
 
    // Query-String basierend auf Filter- und Pagination-Parametern
  const buildQuery = () => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', currentPage);
    if (filters.workshopOrderNumber) {
      params.append('workshopOrderNumber', filters.workshopOrderNumber.value);
    }
    if (filters.status) {
      params.append('status', filters.status.value);
    }
    if (filters.date) {
      params.append('date', filters.date.value);
    }
    if (filters.plannedExecuteDate) {
      params.append('plannedExecuteDate', filters.plannedExecuteDate.value);
    }
    if (filters.customer) {
      params.append('customer', filters.customer.value);
    }
    if (filters.workshopOrderType) {
      params.append('workshopOrderType', filters.workshopOrderType.value);
    }
    if (filters.orderNumber) {
      params.append('orderNumber', filters.orderNumber.value);
    }
    if (filters.blockedPeriodFrom) {
      params.append('blockedPeriodFrom', filters.blockedPeriodFrom.value);
    }
    if (filters.blockedPeriodTo) {
      params.append('blockedPeriodTo', filters.blockedPeriodTo.value);
    }
    if (filters.fulfillmentDate) {  // Neu hinzugefügt
      params.append('fulfillmentDate', filters.fulfillmentDate.value);
    }
    return params.toString() ? `?${params.toString()}` : '';
  };
 
   useEffect(() => {
     const fetchWorkshopOrders = async () => {
       try {
         const query = buildQuery();
         const response = await api.get(`/workshoporders/list${query}`);
         // Erwartetes Format: { total, page, pages, data: [...] }
         setWorkshopOrders(response.data.data);
         setTotalPages(response.data.pages);
       } catch (error) {
         console.error("Fehler beim Abrufen der Workshopaufträge:", error);
       }
     };
     fetchWorkshopOrders();
   }, [filters, currentPage]);

  // Funktion zur Formatierung von Datumsangaben
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind 0-indexiert
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleViewPDF = (orderId) => {
    const pdfUrl = `${api.defaults.baseURL}/documents/workshoporder/${orderId}/pdf`;
    window.open(pdfUrl, '_blank');
  };

  

  // WorkshopOrders inkl. zugehöriger Kontakte laden
  useEffect(() => {
    const fetchWorkshopOrdersWithContacts = async () => {
      try {
        const response = await api.get("/workshoporders");
        const orders = response.data;
        const ordersWithContacts = await Promise.all(
          orders.map(async (order) => {
            if (order.customerId) {
              try {
                const contactsResponse = await api.get(
                  `/contacts/customer/${order.customerId}/`
                );
                const selectedContact = contactsResponse.data.find(
                  (contact) => contact.id.toString() === order.contactId?.toString()
                );
                return { ...order, contact: selectedContact || null };
              } catch (contactError) {
                console.error(`Fehler beim Laden der Kontakte für WorkshopOrder ${order.id}:`, contactError.message);
                return { ...order, contact: null };
              }
            } else {
              return { ...order, contact: null };
            }
          })
        );
        setWorkshopOrders(ordersWithContacts);
      } catch (error) {
        console.error("Fehler beim Laden der WorkshopOrders:", error);
      }
    };

    fetchWorkshopOrdersWithContacts();
  }, []);

  // Initialisiere invoiceStates anhand der vorhandenen Rechnungen in den WorkshopOrders
  useEffect(() => {
    const initializeInvoiceStates = () => {
      const initialStates = {};
      workshopOrders.forEach(order => {
        if (order.invoices && order.invoices.length > 0) {
          const finalExists = order.invoices.some(inv => inv.invoiceType === "finalInvoice");
          initialStates[order.id] = finalExists ? "final" : "partial";
        } else {
          initialStates[order.id] = "none";
        }
      });
      setInvoiceStates(initialStates);
    };

    initializeInvoiceStates();
  }, [workshopOrders]);

  // Lade alle Rechnungen (um den Rechnungsstatus abzugleichen)
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await api.get("/invoices");
        setInvoices(response.data);
      } catch (error) {
        console.error("Fehler beim Laden der Rechnungen:", error);
      }
    };

    fetchInvoices();
  }, []);


  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await api.get('/items');
        setItems(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Artikel:', error);
      }
    };
    fetchItems();
  }, []);

  // WorkshopOrder löschen
  const deleteWorkshopOrder = (id) => {
    if (window.confirm('Möchten Sie diesen Werkstattauftrag wirklich löschen?')) {
      api.delete(`/workshoporders/${id}`)
        .then(() => {
          setWorkshopOrders(prev => prev.filter(order => order.id !== id));
          alert('Werkstattauftrag erfolgreich gelöscht!');
        })
        .catch(error => console.error('Fehler beim Löschen des Werkstattauftrags:', error));
    }
  };

  // Funktion zum Erstellen eines Draft-Werkstattauftrags
  const createDraftWorkshopOrder = async () => {
    try {
      const payload = { customerId: 1, workshopOrderType: 'inspection' }; // Beispielwerte
      const response = await api.post('/workshoporders/draft', payload);
      // Angenommen, response.data enthält den neuen Auftrag als Objekt:
      const newOrder = response.data;
      // Füge den neuen Auftrag an den Anfang der bestehenden Liste hinzu:
      setWorkshopOrders(prevOrders => [newOrder, ...prevOrders]);
      // Navigiere zur Edit-Seite:
      navigate(`/workshoporders/${newOrder.id}/edit`);
    } catch (error) {
      console.error('Fehler beim Erstellen eines Werkstattauftrag-Entwurfs:', error);
      alert('Fehler beim Erstellen des Entwurfs!');
    }
  };
  

  // Funktion zum Erstellen einer Rechnung für einen Workshopauftrag
   // Funktion zum Erstellen einer Rechnung für einen Workshopauftrag (nur finalInvoice)
   // Funktion zum Erstellen einer Rechnung aus einer WorkshopOrder
const createWorkshopOrderInvoice = async (workshopOrderId, invoiceType) => {
  try {
    const payload = { invoiceType }; // invoiceType wird hier z. B. "finalInvoice" sein
    const response = await api.post(
      `/invoices/duplicate-workshoporder/${workshopOrderId}`,
      payload
    );
    alert(`Rechnung erfolgreich erstellt! Rechnung ID: ${response.data.id}`);

    // Update des WorkshopOrders-Arrays: Füge die neue Rechnung zum entsprechenden WorkshopOrder hinzu
    setWorkshopOrders(prevOrders =>
      prevOrders.map(order => {
        if (order.id === workshopOrderId) {
          const newInvoices = order.invoices ? [...order.invoices, response.data] : [response.data];
          return { ...order, invoices: newInvoices };
        }
        return order;
      })
    );

    // Setze den Rechnungsstatus auf "final"
    setInvoiceStates(prevState => ({
      ...prevState,
      [workshopOrderId]: "final"
    }));
  } catch (error) {
    console.error("Fehler beim Erstellen der Rechnung:", error);
    alert("Fehler beim Erstellen der Rechnung!");
  }
};

 // Neue Zustände für Status-Bearbeitung
 const [editingOrderId, setEditingOrderId] = useState(null);
 const [editingStatusValue, setEditingStatusValue] = useState(null);

 // Neue, konstante Statusoptionen
const statusOptions = [
  { value: 'planned', label: 'planned' },
  { value: 'inProgress', label: 'inProgress' },
  { value: 'completed', label: 'completed' },
];

 // Funktion, die beim Klick auf den Status aufgerufen wird
const handleStatusClick = (order) => {
  setEditingOrderId(order.id);
  // Setze den aktuell ausgewählten Status basierend auf dem Order-Status
  setEditingStatusValue({ value: order.status, label: order.status });
};

 // Funktion zum Aktualisieren des Status in der Datenbank
const updateWorkshopOrderStatus = async (orderId, newStatus) => {
  try {
    // Hier wird der WorkshopOrder-Eintrag aktualisiert (angepasst an deinen Backend-Endpoint)
    const response = await api.put(`/workshoporders/${orderId}`, { status: newStatus });
    // Update in der lokalen Liste der WorkshopOrders
    setWorkshopOrders(prevOrders =>
      prevOrders.map(order => (order.id === orderId ? { ...order, status: newStatus } : order))
    );
    setEditingOrderId(null);
    setEditingStatusValue(null);
  } catch (error) {
    console.error('Fehler beim Aktualisieren des Status:', error);
    alert('Status konnte nicht aktualisiert werden.');
  }
};


  // Funktion: Werkstattauftrag per E-Mail senden
  const sendWorkshopOrderMail = async (workshopOrderId) => {
    try {
      await api.post(`/documents/workshoporder/${workshopOrderId}/mail`);
      alert('Werkstattauftrag wurde erfolgreich gemailt!');
    } catch (error) {
      console.error('Fehler beim Mailen des Werkstattauftrags:', error);
      alert('Fehler beim Mailen des Werkstattauftrags!');
    }
  };

  return (
    <div className="workshop-order-list">
      <Helmet>
        <title>WA-Liste</title>
        <meta name="description" content="WA-Liste" />
      </Helmet>
      <h1>Werkstattaufträge</h1>

      {/* Button zum Erstellen eines neuen Werkstattauftrags */}
      <button 
        onClick={createDraftWorkshopOrder}
        style={{ marginBottom: '20px',  }}
      >
        Neuen Werkstattauftrag erstellen
      </button>

      
       {/* Filterleiste */}
       <div style={{ marginBottom: '20px' }}>
        <h3>Filter</h3>
        <div className="filter-grid"
          
        >
          <Select
            options={serverFilterOptions.workshopOrderNumbers?.map(val => ({ value: val, label: val }))}
            placeholder="WA-Nummer"
            isClearable
            value={filters.workshopOrderNumber}
            onChange={(selected) =>
              setFilters({ ...filters, workshopOrderNumber: selected })
            }
          />
          <Select
            options={serverFilterOptions.orderNumbers?.map(val => ({ value: val, label: val }))}
            placeholder="AB-Nummer"
            isClearable
            value={filters.orderNumber}
            onChange={(selected) =>
              setFilters({ ...filters, orderNumber: selected })
            }
          />
          <Select
            options={serverFilterOptions.customers?.map(val => ({ value: val, label: val }))}
            placeholder="Kunde"
            isClearable
            value={filters.customer}
            onChange={(selected) =>
              setFilters({ ...filters, customer: selected })
            }
          />
          <Select
            options={serverFilterOptions.dates?.map(val => ({ value: val, label: val }))}
            placeholder="Erstellungsdatum"
            isClearable
            value={filters.date}
            onChange={(selected) =>
              setFilters({ ...filters, date: selected })
            }
          />
          <Select
            options={serverFilterOptions.plannedExecuteDates?.map(val => ({ value: val, label: val }))}
            placeholder="Datum plan"
            isClearable
            value={filters.plannedExecuteDate}
            onChange={(selected) =>
              setFilters({ ...filters, plannedExecuteDate: selected })
            }
          />
           <Select
    options={serverFilterOptions.fulfillmentDates?.map(val => ({ value: val, label: val }))}  // Neu hinzugefügt
    placeholder="Erfüllungsdatum"
    isClearable
    value={filters.fulfillmentDate}
    onChange={(selected) =>
      setFilters({ ...filters, fulfillmentDate: selected })
    }
  />
          <Select
            options={serverFilterOptions.statuses?.map(val => ({ value: val, label: val }))}
            placeholder="Status"
            isClearable
            value={filters.status}
            onChange={(selected) =>
              setFilters({ ...filters, status: selected })
            }
          />
          <Select
            options={serverFilterOptions.workshopOrderTypes?.map(val => ({ value: val, label: val }))}
            placeholder="WA-Typ"
            isClearable
            value={filters.workshopOrderType}
            onChange={(selected) =>
              setFilters({ ...filters, workshopOrderType: selected })
            }
          />
          <Select
            options={serverFilterOptions.blockedPeriodFromDates?.map(val => ({ value: val, label: val }))}
            placeholder="Sperre von"
            isClearable
            value={filters.blockedPeriodFrom}
            onChange={(selected) =>
              setFilters({ ...filters, blockedPeriodFrom: selected })
            }
          />
          <Select
            options={serverFilterOptions.blockedPeriodToDates?.map(val => ({ value: val, label: val }))}
            placeholder="Sperre bis"
            isClearable
            value={filters.blockedPeriodTo}
            onChange={(selected) =>
              setFilters({ ...filters, blockedPeriodTo: selected })
            }
          />
        </div>
      </div>

      {/* Tabelle mit Werkstattaufträgen */}
{workshopOrders.length > 0 ? (
  
    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>WA-Nummer</th>
          <th>WA-Datum</th>
          <th>Erfüllungsdatum</th>
          <th>WA-Typ</th>
          <th>AB-Nummer</th>
          <th>Maschine</th>
          <th>Datum plan</th>
          <th>Sperre von</th>
          <th>Sperre bis</th>
          <th>Kunde</th>
          <th>Einsatzort</th>
          <th>Status</th>
          <th>Gesamt netto (€)</th>
          <th>Aktionen</th>
          <th>Rechnung</th>
        </tr>
      </thead>
      <tbody>
      {[...workshopOrders]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map(order => {
          // Ermittele die Rechnungen, die zu diesem Werkstattauftrag gehören:
          const invoicesForOrder = invoices.filter(
            inv => inv.workshopOrderId === order.id
          );
          // Bestimme den Rechnungstyp:
          const state = invoicesForOrder.some(
            inv =>
              inv.invoiceType &&
              inv.invoiceType.toLowerCase() === "finalinvoice"
          )
            ? "final"
            : invoicesForOrder.length > 0
            ? "partial"
            : "none";

          return (
            <tr key={order.id}>
              <td>{order.id}</td>
              <td>{order.workshopOrderNumber}</td>
              <td>{formatDate(order.date)}</td>
              <td>{formatDate(order.fulfillmentDate)}</td>
              <td>{order.workshopOrderType}</td>
              <td>{order.orderNumber}</td>
              <td>
                {order.item?.name ||
                  (items.find(item => item.id === order.itemId)?.name) ||
                  'Kein Artikel gefunden'}
              </td>
              <td>{formatDate(order.plannedExecuteDate)}</td>
              <td>{formatDate(order.blockedPeriodFrom)}</td>
              <td>{formatDate(order.blockedPeriodTo)}</td>
              <td>
                {order.customer?.company ||
                  `${order.customer?.firstName} ${order.customer?.lastName}` ||
                  'Unbekannt'}
              </td>
              <td>{order.operationLocation || 'Nicht angegeben'}</td>
              <td>
  {editingOrderId === order.id ? (
    // Inline-Edit: Status-Dropdown wird angezeigt, hier ausschließlich mit statusOptions
    <Select
      autoFocus
      options={statusOptions}
      value={editingStatusValue}
      onChange={(selected) => setEditingStatusValue(selected)}
      onBlur={() => {
        // Wenn der Dropdown verlassen wird, aktualisiere den Status
        if (editingStatusValue && editingStatusValue.value !== order.status) {
          updateWorkshopOrderStatus(order.id, editingStatusValue.value);
        } else {
          setEditingOrderId(null);
        }
      }}
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  ) : (
    // Status wird als klickbarer Text angezeigt
    <span onClick={() => handleStatusClick(order)} style={{ cursor: 'pointer' }}>
      {order.status}
    </span>
  )}
</td>
              <td>{order.totalNet?.toFixed(2).replace('.', ',')} €</td>
              <td>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {/* NEU: WA-PDF-Ansicht (Auge auf grünem Grund) */}
    <button
      type="button"
      onClick={() => handleViewPDF(order.id)}
      title="Werkstattauftrag ansehen"
      style={{
        fontSize: '15px',
        padding: '5px 10px',
        background: '#28a745', // grün
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      }}
    >
      👁️
    </button>

    {/* Bestehender Edit-Button */}
    <button
      type="button"
      onClick={() => navigate(`/workshoporders/${order.id}/edit`)}
      style={{
        fontSize: '15px',
        padding: '5px 10px',
        background: '#007bff',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      }}
    >
      edit
    </button>

    {/* Bestehender Löschen-Button */}
    <button
      type="button"
      onClick={() => deleteWorkshopOrder(order.id)}
      style={{
        fontSize: '15px',
        padding: '5px 10px',
        background: '#dc3545',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      }}
    >
      X
    </button>
                </div>
              </td>
              <td>
                {state !== "final" ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {state === "none" && (
                      <button
                        type="button"
                        onClick={() => createWorkshopOrderInvoice(order.id, "finalInvoice")}
                        style={{
                          fontSize: '15px',
                          padding: '5px 10px',
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        Rechnung
                      </button>
                    )}
                  </div>
                ) : (
                  <span style={{ color: 'green', fontWeight: 'bold' }}>erstellt</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
 
) : (
  <p>Keine Werkstattaufträge verfügbar.</p>
)}



<div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
  <button
    onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
    disabled={currentPage === 1}
    style={{
      fontSize: '15px',
      padding: '8px 12px',
      backgroundColor:'rgb(155, 155, 155)',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer'
    }} 
  >
    Vorherige
  </button>

  {/* Anzeige der aktuellen Seite */}
  <span>
    Seite {currentPage} von {totalPages}
  </span>

  <button
    onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
    disabled={currentPage === totalPages}
    style={{
      fontSize: '15px',
      padding: '8px 12px',
      backgroundColor:'rgb(155, 155, 155)',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer'
    }} 
  >
    Nächste
  </button>
</div>

      
    </div>
  );
};

export default WorkshopOrderList;
